import React, { useEffect, useRef, useState } from "react";
import Peer from "simple-peer";
import { useHistory, useLocation, Prompt } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
// Styles
import {
  Page, TopBar, Body, VideoColumn, Stage, Tile, Video, Icon,
  ControlBar, ControlGroup, CircleButton, HangUpButton, TileButton,
  ChatColumn, StatusBar, AnswerButton,
} from "./styles";
// Assets
import VippstarLogo from "../../assets/vippstar_logo.png";
import IcMonitor from "../../assets/video_call/monitor.png";
import IcMonitorOff from "../../assets/video_call/monitor_off.png";
import IcVideo from "../../assets/video_call/video.png";
import IcVideoOff from "../../assets/video_call/video_off.png";
import IcMic from "../../assets/video_call/mic.png";
import IcMicOff from "../../assets/video_call/mic_off.png";
import IcPhoneOff from "../../assets/video_call/phone-off.png";
import IcMaximize from "../../assets/video_call/maximize.png";
import IcMinimize from "../../assets/video_call/minimize.png";
import IcSwitch from "../../assets/video_call/switch.png";
import ringToneSrc from "../../assets/ring_tone.wav";
import ringbackToneSrc from "../../assets/ringback_tone.wav";
import hangUpSrc from "../../assets/hang_up.wav";
// Components
import CaregiverChat from "../caregiverChat";
import { pathHomePage } from "../../config/constants";
import socket from "../../socket";

// simple-peer notifica el cierre del otro extremo a través del evento "error".
// No es un fallo: es la otra parte colgando.
const isNormalClose = (err) =>
  err?.code === "ERR_DATA_CHANNEL" ||
  /close called|user-initiated abort/i.test(err?.message || "");


const ICE_SERVERS = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
  ],
};

function VideoChat() {
  const { t } = useTranslation();
  const [stream, setStream] = useState();
  const [receivingCall, setReceivingCall] = useState(false);
  const [caller, setCaller] = useState("");
  const [callerName, setCallerName] = useState("");
  const [callerSignal, setCallerSignal] = useState();
  const [callerUserId, setCallerUserId] = useState(null);
  const [callerSessionId, setCallerSessionId] = useState(null);
  const [callAccepted, setCallAccepted] = useState(false);
  const [callEnded, setCallEnded] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [remoteUserId, setRemoteUserId] = useState(null);
  const [selectedUser, setSelectedUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("selectedUser")) || null; }
    catch { return null; }
  });
  const [isCalling, setIsCalling] = useState(false);
  // Modo ampliado: un vídeo grande y el otro en miniatura, intercambiables.
  const [expanded, setExpanded] = useState(false);
  const [swapped, setSwapped] = useState(false);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const myVideo = useRef();
  const userVideo = useRef();
  const connectionRef = useRef();
  const screenTrackRef = useRef();
  const pendingCallRef = useRef(null);
  const pendingAnswerRef = useRef(null);
  // El paciente entra a esperar: al recibir la oferta la contesta solo, sin
  // pedirle un segundo clic (ya aceptó al pulsar Join).
  const waitingRef = useRef(false);
  // Contestamos solos: no hay que hacer sonar el tono de llamada entrante.
  const autoAnsweringRef = useRef(false);
  // Los listeners de socket se registran una sola vez, así que no pueden leer
  // el estado directamente: se quedarían con el valor del primer render.
  const callStateRef = useRef({ calling: false, accepted: false });
  // Evita duplicar el aviso de "llamada terminada".
  const endHandledRef = useRef(false);
  // Estado de los medios: `ready` puede ser true con `stream` a null (modo
  // espectador), así que no vale con mirar si hay stream.
  const mediaRef = useRef({ ready: false, stream: null });
  const sessionIdRef = useRef(null);
  const ringToneRef = useRef(null);
  const ringbackToneRef = useRef(null);
  const hangUpRef = useRef(null);
  const wasInCallRef = useRef(false);

  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const myName = currentUser.display_name || currentUser.username || "";
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    if (selectedUser) localStorage.setItem("selectedUser", JSON.stringify(selectedUser));
    else localStorage.removeItem("selectedUser");
  }, [selectedUser]);

  // Init audio elements
  useEffect(() => {
    const ring = new Audio(ringToneSrc);
    ring.loop = true;
    ringToneRef.current = ring;
    const ringback = new Audio(ringbackToneSrc);
    ringback.loop = true;
    ringbackToneRef.current = ringback;
    hangUpRef.current = new Audio(hangUpSrc);
    return () => {
      ring.pause(); ring.currentTime = 0;
      ringback.pause(); ringback.currentTime = 0;
    };
  }, []);

  useEffect(() => {
    callStateRef.current = { calling: isCalling, accepted: callAccepted };
  }, [isCalling, callAccepted]);

  // Ringback tone: caller hears this while waiting for answer
  useEffect(() => {
    const ringback = ringbackToneRef.current;
    if (!ringback) return;
    if (isCalling && !callAccepted) {
      ringback.currentTime = 0;
      ringback.play().catch(() => {});
    } else {
      ringback.pause();
      ringback.currentTime = 0;
    }
  }, [isCalling, callAccepted]);

  // Ring tone: receiver hears this when incoming call
  useEffect(() => {
    const ring = ringToneRef.current;
    if (!ring) return;
    if (receivingCall && !callAccepted && !autoAnsweringRef.current) {
      ring.currentTime = 0;
      ring.play().catch(() => {});
    } else {
      ring.pause();
      ring.currentTime = 0;
    }
  }, [receivingCall, callAccepted]);

  // Hang up sound: play when call ends (only if there was an active call)
  useEffect(() => {
    if (callAccepted && !callEnded) {
      wasInCallRef.current = true;
    }
    if (wasInCallRef.current && !callAccepted) {
      hangUpRef.current?.play().catch(() => {});
      wasInCallRef.current = false;
    }
  }, [callAccepted, callEnded]);

  // Ejecuta la llamada o la respuesta que llegó por location.state.
  const consumePending = (currentStream) => {
    if (pendingCallRef.current) {
      const { patient, sessionId: sid } = pendingCallRef.current;
      pendingCallRef.current = null;
      callUser(patient, sid, currentStream);
    } else if (pendingAnswerRef.current) {
      const d = pendingAnswerRef.current;
      pendingAnswerRef.current = null;
      answerCall({ signal: d.signal, from: d.from, callerUserId: d.fromUserId || null, callerSessionId: d.sessionId || null, callerName: d.name || "", stream: currentStream });
    }
  };

  // Depende de location.key, no de [], porque navegar a esta misma ruta (por
  // ejemplo al contestar desde el popup estando ya aquí) NO remonta el
  // componente: el estado nuevo llegaría y nadie lo leería.
  useEffect(() => {
    if (location.state?.sessionId) {
      pendingCallRef.current = {
        patient: location.state.patient,
        sessionId: location.state.sessionId,
      };
    }
    if (location.state?.pendingAnswer) {
      pendingAnswerRef.current = location.state.pendingAnswer;
    }
    if (location.state?.waitForSessionId) {
      waitingRef.current = true;
      socket.emit("waitingInRoom", { sessionId: location.state.waitForSessionId });
    }
    // Si el componente ya está montado y los medios resueltos, disparar ahora;
    // si acaba de montarse, lo hará el efecto de getUserMedia.
    if (mediaRef.current.ready) consumePending(mediaRef.current.stream || undefined);
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // La llamada NO puede depender de tener cámara: si el dispositivo está
    // ocupado (otra pestaña, otra app) o denegado, hay que entrar igualmente
    // en modo solo-escucha en vez de descartar la llamada en silencio.
    const getMedia = async () => {
      try {
        return await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      } catch (err) {
        console.warn("Cámara y micrófono no disponibles:", err.name, err.message);
        try {
          const audioOnly = await navigator.mediaDevices.getUserMedia({ audio: true });
          toast.warn(t("call.errors.audioOnly"));
          return audioOnly;
        } catch (err2) {
          console.warn("Micrófono no disponible:", err2.name, err2.message);
          toast.error(t("call.errors.noMedia", { name: err2.name }));
          return null;
        }
      }
    };

    getMedia().then((currentStream) => {
      mediaRef.current = { ready: true, stream: currentStream };
      if (currentStream) {
        setStream(currentStream);
        if (myVideo.current) myVideo.current.srcObject = currentStream;
      }
      consumePending(currentStream || undefined);
    });

    const authenticate = () => {
      const token = localStorage.getItem("token");
      if (token) socket.emit("authenticate", token);
    };

    // Si el socket ya está conectado (evento "me" llegó antes de montar el componente)
    if (socket.connected) authenticate();

    socket.on("me", authenticate);

    const handleCallUser = (data) => {
      setReceivingCall(true);
      setCaller(data.from);
      setCallerName(data.name || t("call.unknownUser"));
      setCallerSignal(data.signal);
      if (data.fromUserId) setCallerUserId(data.fromUserId);
      if (data.sessionId) setCallerSessionId(data.sessionId);

      // Ya estaba esperando en la sala: contestar sin pedir otro clic.
      if (waitingRef.current) {
        waitingRef.current = false;
        autoAnsweringRef.current = true;
        pendingAnswerRef.current = data;
        if (mediaRef.current.ready) consumePending(mediaRef.current.stream || undefined);
      }
    };

    // Un paciente espera en la sala: el terapeuta inicia la llamada.
    const handlePatientWaiting = (data) => {
      if (callStateRef.current.accepted) return; // ya hablando con alguien

      // Si ya había una oferta en curso, se emitió cuando el paciente aún no
      // estaba en la sala: no la contestará nadie. Se descarta y se rehace.
      if (connectionRef.current) {
        try { connectionRef.current.destroy(); } catch { /* peer ya cerrado */ }
        connectionRef.current = null;
      }

      const target = { id: data.patientId, label: data.patientCode };
      if (mediaRef.current.ready) {
        callUser(target, data.sessionId, mediaRef.current.stream || undefined);
      } else {
        pendingCallRef.current = { patient: target, sessionId: data.sessionId };
      }
    };

    const handleCancelled = () => resetCall();
    const handleRejected = () => resetCall();

    // Aviso del servidor: la otra parte ha colgado.
    const handleEnded = () => remoteHangup();

    // El destinatario no tiene socket registrado: no está conectado.
    const handleUnavailable = () => {
      resetCall();
      toast.error(t("call.errors.unavailable"));
    };

    socket.on("callUser", handleCallUser);
    socket.on("callCancelled", handleCancelled);
    socket.on("callRejected", handleRejected);
    socket.on("callUnavailable", handleUnavailable);
    socket.on("patientWaiting", handlePatientWaiting);
    socket.on("callEnded", handleEnded);

    return () => {
      socket.off("callUser", handleCallUser);
      socket.off("callCancelled", handleCancelled);
      socket.off("callRejected", handleRejected);
      socket.off("callUnavailable", handleUnavailable);
      socket.off("patientWaiting", handlePatientWaiting);
      socket.off("callEnded", handleEnded);
      socket.off("me", authenticate);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fin de llamada provocado por el otro extremo. Puede enterarse por dos vías
  // (aviso del servidor y cierre del canal WebRTC): avisamos una sola vez.
  const remoteHangup = () => {
    if (endHandledRef.current) return;
    endHandledRef.current = true;
    toast.info(t("call.ended"));
    if (connectionRef.current) {
      try { connectionRef.current.destroy(); } catch { /* ya cerrado */ }
      connectionRef.current = null;
    }
    resetCall();
  };

  const resetCall = () => {
    autoAnsweringRef.current = false;
    setExpanded(false);
    setSwapped(false);
    setCallAccepted(false);
    setReceivingCall(false);
    setCallEnded(false);
    setIsCalling(false);
    setCaller("");
    setCallerName("");
    setCallerSignal(null);
    setRemoteUserId(null);
    setCallerUserId(null);
    setCallerSessionId(null);
    sessionIdRef.current = null;
    if (screenTrackRef.current) stopScreenSharing();
    if (userVideo.current) userVideo.current.srcObject = null;
    connectionRef.current = null;
  };

  const callUser = (overrideUser = null, overrideSid = null, streamOverride = null) => {
    const user = overrideUser || selectedUser;
    const sid = overrideSid !== null ? overrideSid : sessionIdRef.current;
    const activeStream = streamOverride || stream;
    if (!user) return;
    if (overrideUser) setSelectedUser(overrideUser);
    if (overrideSid !== null) sessionIdRef.current = overrideSid;
    endHandledRef.current = false;
    setIsCalling(true);
    const peer = new Peer({ initiator: true, trickle: false, stream: activeStream, config: ICE_SERVERS });

    peer.on("signal", (data) => {
      socket.emit("callUser", {
        userToCall: user.id,
        signalData: data,
        from: socket.id,
        name: myName,
        fromUserId: currentUser.id,
        sessionId: sid || null,
      });
    });

    peer.on("stream", (remoteStream) => {
      if (userVideo.current) userVideo.current.srcObject = remoteStream;
    });

    peer.on("data", (data) => {
      const msg = JSON.parse(data);
      // El otro comparte pantalla: la ampliamos para que se lea.
      if (msg.type === "SCREEN_SHARE_START") { setSwapped(false); setExpanded(true); }
      else if (msg.type === "SCREEN_SHARE_STOP") setExpanded(false);
    });

    peer.on("error", (err) => {
      if (isNormalClose(err)) {
        remoteHangup();
        return;
      }
      console.error("Error de conexión:", err.code, err.message);
      toast.error(t("call.errors.connection", { message: err.message }));
      resetCall();
    });
    peer.on("close", () => resetCall());

    socket.off("callAccepted");
    socket.on("callAccepted", (data) => {
      setIsCalling(false);
      setCallAccepted(true);
      if (data.fromUserId) setRemoteUserId(data.fromUserId);
      peer.signal(data.signal ?? data);
    });

    connectionRef.current = peer;
  };

  const answerCall = (opts = null) => {
    const signal = opts?.signal ?? callerSignal;
    const from = opts?.from ?? caller;
    if (!signal || !from) {
      console.error("No hay oferta que contestar", { signal: !!signal, from });
      toast.error(t("call.errors.lostOffer"));
      return;
    }
    const userId = opts?.callerUserId !== undefined ? opts.callerUserId : callerUserId;
    const sid = opts?.callerSessionId !== undefined ? opts.callerSessionId : callerSessionId;
    const activeStream = opts?.stream ?? stream;

    if (opts?.callerName) setCallerName(opts.callerName);
    if (sid) sessionIdRef.current = sid;
    endHandledRef.current = false;
    setCallAccepted(true);
    setRemoteUserId(userId);

    const peer = new Peer({ initiator: false, trickle: false, stream: activeStream, config: ICE_SERVERS });

    peer.on("signal", (data) => {
      socket.emit("answerCall", { signal: data, to: from, fromUserId: currentUser.id, sessionId: sid || null });
    });

    peer.on("stream", (remoteStream) => {
      if (userVideo.current) userVideo.current.srcObject = remoteStream;
    });

    peer.on("data", (data) => {
      const msg = JSON.parse(data);
      // El otro comparte pantalla: la ampliamos para que se lea.
      if (msg.type === "SCREEN_SHARE_START") { setSwapped(false); setExpanded(true); }
      else if (msg.type === "SCREEN_SHARE_STOP") setExpanded(false);
    });

    peer.on("error", (err) => {
      if (isNormalClose(err)) {
        remoteHangup();
        return;
      }
      console.error("Error de conexión:", err.code, err.message);
      toast.error(t("call.errors.connection", { message: err.message }));
      resetCall();
    });
    peer.on("close", () => resetCall());

    peer.signal(signal);
    connectionRef.current = peer;
  };

  const shareScreen = () => {
    navigator.mediaDevices.getDisplayMedia({ cursor: true }).then((displayStream) => {
      const screenTrack = displayStream.getVideoTracks()[0];
      screenTrackRef.current = screenTrack;
      if (connectionRef.current && stream) {
        const cameraTrack = stream.getVideoTracks()[0];
        if (cameraTrack) { connectionRef.current.replaceTrack(cameraTrack, screenTrack, stream); cameraTrack.stop(); }
        connectionRef.current.send(JSON.stringify({ type: "SCREEN_SHARE_START" }));
      }
      if (myVideo.current) myVideo.current.srcObject = displayStream;
      setIsScreenSharing(true);
      screenTrack.onended = () => stopScreenSharing();
    }).catch((err) => console.warn("No se pudo compartir la pantalla:", err.message));
  };

  const stopScreenSharing = async () => {
    const oldScreenTrack = screenTrackRef.current;
    if (screenTrackRef.current) { screenTrackRef.current.stop(); screenTrackRef.current = null; }
    try {
      const newCameraStream = await navigator.mediaDevices.getUserMedia({ video: true });
      const newCameraTrack = newCameraStream.getVideoTracks()[0];
      if (stream) {
        const dead = stream.getVideoTracks()[0];
        if (dead) stream.removeTrack(dead);
        stream.addTrack(newCameraTrack);
      }
      if (connectionRef.current && !connectionRef.current.destroyed) {
        if (oldScreenTrack) connectionRef.current.replaceTrack(oldScreenTrack, newCameraTrack, stream);
        connectionRef.current.send(JSON.stringify({ type: "SCREEN_SHARE_STOP" }));
      }
    } catch (err) { console.warn("No se pudo reactivar la cámara:", err.message); }
    if (myVideo.current) myVideo.current.srcObject = stream || null;
    setIsScreenSharing(false);
  };

  const leaveCall = () => {
    if (!callAccepted) {
      socket.emit("cancelCall");
    }
    // Siempre, aunque no haya sesión: es lo que avisa al otro extremo de que
    // hemos colgado, sin depender de que se entere por el cierre del canal.
    endHandledRef.current = true; // colgamos nosotros: no anunciarlo como ajeno
    socket.emit("endCall", {
      sessionId: sessionIdRef.current || null,
      to: remoteUserId || null,
    });
    if (connectionRef.current) connectionRef.current.destroy();
    resetCall();

    // En el siguiente tick: así el <Prompt> ya se ha vuelto a renderizar con la
    // llamada terminada y no pide confirmación para salir de una sala vacía.
    setTimeout(() => {
      if (history.length > 1) history.goBack();
      else history.push(pathHomePage);
    }, 0);
  };

  // Cortar la pista es instantáneo para el otro extremo y no renegocia nada.
  const toggleTrack = (kind) => {
    const tracks = kind === "audio" ? stream?.getAudioTracks() : stream?.getVideoTracks();
    const track = tracks?.[0];
    if (!track) return;
    track.enabled = !track.enabled;
    (kind === "audio" ? setMicOn : setCamOn)(track.enabled);
  };

  const inCall = callAccepted && !callEnded;
  // En modo ampliado uno de los dos vídeos pasa a miniatura; `swapped` decide cuál.
  const remoteIsMain = !swapped;

  const controls = (
    <ControlBar $floating={expanded}>
      <ControlGroup>
        <CircleButton
          type="button"
          title={isScreenSharing ? t("call.stopSharing") : t("call.shareScreen")}
          $off={isScreenSharing}
          disabled={!inCall}
          onClick={isScreenSharing ? stopScreenSharing : shareScreen}
        >
          <Icon src={isScreenSharing ? IcMonitorOff : IcMonitor} alt="" />
        </CircleButton>
        <CircleButton
          type="button"
          title={camOn ? t("call.cameraOff") : t("call.cameraOn")}
          $off={!camOn}
          disabled={!stream}
          onClick={() => toggleTrack("video")}
        >
          <Icon src={camOn ? IcVideo : IcVideoOff} alt="" />
        </CircleButton>
        <CircleButton
          type="button"
          title={micOn ? t("call.micOff") : t("call.micOn")}
          $off={!micOn}
          disabled={!stream}
          onClick={() => toggleTrack("audio")}
        >
          <Icon src={micOn ? IcMic : IcMicOff} alt="" />
        </CircleButton>
      </ControlGroup>

      <ControlGroup>
        {expanded ? (
          <CircleButton type="button" title={t("call.minimize")} onClick={() => setExpanded(false)}>
            <Icon src={IcMinimize} alt="" />
          </CircleButton>
        ) : (
          <HangUpButton type="button" onClick={leaveCall}>
            <Icon src={IcPhoneOff} alt="" $size={18} />
            {t("call.hangUpAndLeave")}
          </HangUpButton>
        )}
      </ControlGroup>
    </ControlBar>
  );

  return (
    <Page>
      <Prompt
        when={(callAccepted && !callEnded) || (receivingCall && !callAccepted)}
        message={receivingCall && !callAccepted
          ? t("call.leaveIncomingCall")
          : t("call.promptActive")}
      />

      <TopBar>
        <img src={VippstarLogo} alt="Vippstar" style={{ width: 92 }} />
      </TopBar>

      <Body>
        <VideoColumn $expanded={expanded}>
          <Stage $expanded={expanded}>
            {/* Los dos vídeos se mantienen montados siempre: desmontarlos
                perdería el srcObject y habría que renegociar la llamada. */}
            <Tile $expanded={expanded} $pip={expanded && !remoteIsMain} $hidden={!inCall}>
              <Video playsInline ref={userVideo} autoPlay />
              {!expanded && (
                <TileButton
                  type="button"
                  title={t("call.maximize")}
                  onClick={() => { setSwapped(false); setExpanded(true); }}
                >
                  <Icon src={IcMaximize} alt="" $size={34} />
                </TileButton>
              )}
              {expanded && !remoteIsMain && (
                <TileButton type="button" title={t("call.swap")} onClick={() => setSwapped(false)}>
                  <Icon src={IcSwitch} alt="" $size={34} />
                </TileButton>
              )}
            </Tile>

            <Tile $expanded={expanded} $pip={expanded && remoteIsMain}>
              <Video playsInline muted ref={myVideo} autoPlay />
              {!expanded && (
                <TileButton
                  type="button"
                  title={t("call.maximize")}
                  onClick={() => { setSwapped(true); setExpanded(true); }}
                >
                  <Icon src={IcMaximize} alt="" $size={34} />
                </TileButton>
              )}
              {expanded && remoteIsMain && (
                <TileButton type="button" title={t("call.swap")} onClick={() => setSwapped(true)}>
                  <Icon src={IcSwitch} alt="" $size={34} />
                </TileButton>
              )}
            </Tile>

            {expanded && controls}
          </Stage>

          {!expanded && controls}

          {isCalling && !inCall && (
            <StatusBar>{t("call.calling", { name: selectedUser?.label || "" })}</StatusBar>
          )}
          {receivingCall && !callAccepted && (
            <StatusBar>
              {t("call.incoming", { name: callerName })}
              <AnswerButton type="button" onClick={answerCall}>{t("call.answer")}</AnswerButton>
            </StatusBar>
          )}
        </VideoColumn>

        <ChatColumn $hidden={expanded}>
          <CaregiverChat
            title={t("caregiver.chatWith", {
              code: selectedUser ? selectedUser.label : callerName || "",
            })}
            currentUserId={currentUser.id}
            otherUserId={remoteUserId || (callAccepted ? null : selectedUser?.id)}
          />
        </ChatColumn>
      </Body>
    </Page>
  );
}

export default VideoChat;
